import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { LocationFeatureV2 } from 'models/location-feature.model';
import { selectCurrentLocation, selectCurrentLocationV2 } from 'redux/actions/location';
import { selectOfficeConfigurations } from 'redux/actions/customization/customization.action';
import { selectFeatureFlags } from 'redux/actions/feature-flags/feature-flags.reducer';
import { fetchLocationFeatures } from 'components/customization-page-container/customization-page.queries';
import { MetricEvalContext } from '../messaging-air-metrics.types';
import { MessagingAirHandoffQueryKeys } from '../messaging-air-handoff.queries';
import { VoiceAirHandoffQueryKeys } from '../../voice-air-handoff/voice-air-handoff.queries';
import { MESSAGING_AIR_METRIC_DEFINITIONS } from '../messaging-air-metric-definitions';
import {
  ChecklistMetricItem,
  ChecklistMetricStatus,
} from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import {
  initializeChecklistMetrics,
  blockChecklistMetrics,
  evaluateMetricDefinition,
} from '../../handoff-checklist-v2/handoff-checklist-v2.helpers';

const createInitialMetrics = (): ChecklistMetricItem[] =>
  MESSAGING_AIR_METRIC_DEFINITIONS.map((def) => ({
    id: def.id,
    name: def.name,
    isRequired: def.isRequired,
    status: ChecklistMetricStatus.PENDING,
    isLoading: true,
    criteria: def.criteria.map((c) => ({
      description: c.description,
      status: ChecklistMetricStatus.PENDING,
    })),
  }));

export const useMessagingAirHandoffMetrics = (handoffId?: string) => {
  const [metrics, setMetrics] = useState<ChecklistMetricItem[]>(createInitialMetrics);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const currentLocation = useSelector(selectCurrentLocation);
  const locationId = currentLocation?.LocationID;
  const locationV2 = useSelector(selectCurrentLocationV2);
  const orgId = locationV2?.orgId;
  const featureFlags = useSelector(selectFeatureFlags);
  // Track the current 'generation' of fetchMetrics calls to prevent stale async responses
  // (e.g. from a previous location change or refresh) from updating state.
  const generationRef = useRef<number>(0);
  const currentMetricsRef = useRef<ChecklistMetricItem[]>([]);

  useEffect(() => {
    currentMetricsRef.current = metrics;
  }, [metrics]);

  const fetchMetrics = useCallback(async () => {
    if (!locationId) {
      setError('No location selected');
      setIsLoading(false);
      return;
    }

    const generation = ++generationRef.current;
    setIsLoading(true);
    setError(null);

    try {
      await Promise.all([
        queryClient.invalidateQueries(MessagingAirHandoffQueryKeys.baseQueryKey),
        queryClient.invalidateQueries(VoiceAirHandoffQueryKeys.baseQueryKey),
      ]);

      if (generation !== generationRef.current) return;

      // Keep the checklist pending until the flags are freshly loaded so stale
      // passing results can't enable Submit.
      const pendingMetrics = initializeChecklistMetrics(
        MESSAGING_AIR_METRIC_DEFINITIONS,
        { locationId, orgId, handoffId, queryClient } as MetricEvalContext,
        currentMetricsRef.current
      );
      setMetrics(pendingMetrics);
      currentMetricsRef.current = pendingMetrics;

      let features: LocationFeatureV2[];
      try {
        features = await fetchLocationFeatures(queryClient, locationId, { force: true });
      } catch {
        if (generation === generationRef.current) {
          setMetrics(
            blockChecklistMetrics(pendingMetrics, 'Failed to refresh customization flags')
          );
          setError('Failed to refresh customization flags');
          setIsLoading(false);
        }
        return;
      }
      if (generation !== generationRef.current) return;

      const ctx: MetricEvalContext = {
        locationId,
        orgId,
        handoffId,
        queryClient,
        customizationFeatures: features,
        featureFlags: featureFlags || [],
        officeConfigurations: selectOfficeConfigurations.resultFunc(features),
      };

      setIsLoading(false);

      // Concurrently evaluate each metric and update state in a staggered fashion
      MESSAGING_AIR_METRIC_DEFINITIONS.forEach(async (def) => {
        try {
          const existingMetric = currentMetricsRef.current.find((m) => m.id === def.id);
          const { overallStatus, evaluatedCriteria } = await evaluateMetricDefinition(
            def,
            ctx,
            existingMetric
          );

          if (generation === generationRef.current) {
            setMetrics((prev) =>
              prev.map((m) =>
                m.id === def.id
                  ? {
                      ...m,
                      isLoading: false,
                      status: overallStatus,
                      criteria: evaluatedCriteria,
                    }
                  : m
              )
            );
          }
        } catch (err) {
          if (generation === generationRef.current) {
            setMetrics((prev) =>
              prev.map((m) =>
                m.id === def.id
                  ? {
                      ...m,
                      isLoading: false,
                      status: ChecklistMetricStatus.BLOCKED,
                      criteria: m.criteria.map((c) => ({
                        ...c,
                        status: ChecklistMetricStatus.BLOCKED,
                        reason:
                          (err instanceof Error ? err.message : '') ||
                          'Error running evaluate',
                      })),
                    }
                  : m
              )
            );
          }
        }
      });
    } catch (err) {
      if (generation === generationRef.current) {
        setError(
          (err instanceof Error ? err.message : '') ||
            'Failed to initialize metric checking'
        );
        setIsLoading(false);
      }
    }
  }, [locationId, orgId, featureFlags, queryClient, handoffId]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    setMetrics,
    isLoading,
    error,
    refresh: fetchMetrics,
  };
};
